import { useEnvContext } from "contexts/EnvProvider";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import {
	IShopLoyaltyOffer,
	IShopPurchaseOffer
} from "contexts/WebStoreProvider/types";
import { FeaturesFlags } from "helpers";
import { useAppDispatch, useAppSelector } from "hooks";
import { useLinks } from "hooks/useLinks";
import { Config, DipButton, WsDialogs } from "interfaces";
import { IOffer } from "pages/StoreFront/lib/SingleItems";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { offersApi, useGetOffersQuery } from "redux/apis";
import {
	claimDipReward,
	onAddRequisitionCount,
	onCloseDIP,
	onDisableButtonsDIP,
	onEnableButtonsDIP,
	onOpenPopUp,
	onScrollToShopSection,
	onSetBundleUpDip,
	selectDipState,
	selectPopUpManagerState,
	selectWebStoreState,
	sendDipAction,
	setOpen,
	ShopSectionTitles
} from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { selectShopSectionsState } from "redux/slices/shop-sections";
import { getTimeLeft } from "utils/time";
import { getCloseClickTrackingParams, getDipAnalyticsType } from "../helper";
import { DisplayReward } from "../types";

interface DIPRewards {
	/**
	 *  @function onAction
	 * 	@interface (button:DipButton) => void
	 * 	@description Receive a button and do two actions:
	 * 		1. Send to server the action that user did
	 * 		2. Evaluate `actionType` property to know what webstore has to do: navigate, init a purchase, claim a reward, etc
	 */
	onAction: (button: DipButton, price?: number) => void;
}

export const useDIPAction = (): DIPRewards => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { open } = usePopUpsV2();
	const { openDialog } = useDialogModalRedux();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { currentDip, fromHud, activeTrigger } = useAppSelector(selectDipState);
	const { variant: popup } = useAppSelector(selectPopUpManagerState);
	const { rewards } = useAppSelector(selectWebStoreState).store;
	const { authToken, nowTime } = useWebStoreContext();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { data: loyaltyOffers } = useGetOffersQuery({
		offerType: "loyalty",
		isAuthenticated: authToken?.value ? true : false
	});
	const links = useLinks();
	const {
		userTracker,
		wsStoreBuy,
		suppressOrderConfirmation,
		postUserMetadata
	} = useWebStoreContext();
	const { shopSections } = useAppSelector(selectShopSectionsState);
	const { data: purchaseOffers } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: !!authToken?.value
	});

	const location = useLocation();

	const isFirstTimeLPFTUESeen = useMemo(() => {
		return userTracker?.metadata?.hasSeenLPTutorial === "true";
	}, []);

	const handleCloseClick = () => {
		dispatch(onCloseDIP());
		reportEvent("dialog_x_close", {
			source: "dips_dialog",
			...getCloseClickTrackingParams(currentDip?.dip.id, currentDip?.buttons)
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "dialog_x_close",
			event_type: "dialog_x_close",
			action: "click",
			source: "dips_dialog",
			event_id: currentDip?.dip.id,
			offer_id: currentDip?.buttons?.map((b) => b.offerId).join(", "),
			page_location: location.pathname
		});
	};

	const openCustomerSupport = () => {
		const myWindow = window as typeof window & {
			Helpshift?: (apiName: string, ...args: unknown[]) => void;
		};

		if (!myWindow.Helpshift) {
			return;
		}

		myWindow.Helpshift("open");
		dispatch(onCloseDIP());
	};

	useEffect(() => {
		if (
			currentDip?.requisitionCount !== undefined &&
			currentDip?.dip.maxRequisitions !== undefined &&
			currentDip?.requisitionCount === currentDip?.dip.maxRequisitions
		) {
			handleCloseClick();
		}
	}, [currentDip?.requisitionCount]);

	const onAction = (button: DipButton | undefined, price?: number): void => {
		if (typeof button === "undefined") {
			return;
		}
		// prettier-ignore
		const {actionType, id, loyaltyOfferId, deeplinkUrl, offerId, shopSection, rewardId, externalUrl} = button;

		/**🔦 Sending the action executed by user to server */
		dispatch(
			sendDipAction({
				actionType: "clickedButton",
				buttonId: id ?? ""
			})
		);

		/**🔦 Evaluating what webstore has to do depending actionType */
		switch (actionType) {
			case "deeplink":
				window.open(deeplinkUrl, "_blank");
				dispatch(onCloseDIP());
				break;
			case "externalUrl":
				window.open(externalUrl, "_blank");
				dispatch(onCloseDIP());
				break;
			case "customerSupport":
				openCustomerSupport();
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "dips_button_click",
					event_type: "dips_dialog",
					action: actionType,
					source: "dips_dialog",
					event_id: currentDip?.dip.id,
					page_location: location.pathname
				});
				break;
			case "closeDip":
				dispatch(onCloseDIP());
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "dips_button_click",
					event_type: "dips_dialog",
					action: actionType,
					source: "dips_dialog",
					event_id: currentDip?.dip.id,
					page_location: location.pathname
				});
				break;
			case "grantReward":
			case "grantLoyaltyReward":
				reportEvent("dips_button_click", {
					source: "dips_dialog",
					dip_id: currentDip?.dip.id,
					reward_id: rewardId,
					page_location: location.pathname
				});
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "dips_button_click",
					event_type: "dips_dialog",
					action: actionType,
					source: "dips_dialog",
					event_id: currentDip?.dip.id,
					reward_id: rewardId,
					page_location: location.pathname
				});
				dispatch(
					claimDipReward({
						dipId: currentDip?.dip.id as string,
						buttonId: id,
						grantRewardType: actionType
					})
				);
				break;
			case "loyaltyPurchase":
				//const loyalty = loyaltyOffers.find((c) => c.id === loyaltyOfferId);
				const loyalty = loyaltyOffers.offers.find(
					(c: Config) => c.id === loyaltyOfferId
				);
				if (loyalty) {
					const {
						description,
						loyaltyPointPrice,
						rewardId,
						rewardImageUrl,
						id
					} = loyalty;
					const reward = rewards[rewardId];
					const rewardItems = reward ? reward.items : [];
					/**🔦 We check if user has enough loyalty points to buy the offer */
					// @ts-ignore
					if (userTracker?.loyaltyPoints < loyaltyPointPrice) {
						openDialog(WsDialogs.INSUFFICIENT_LOYALTY_POINT_BALANCE, {
							NeededLoyaltyPoints: loyaltyPointPrice,
							CurrentLoyaltyPoints: userTracker?.loyaltyPoints
						});
						return;
					}
					const dipId = currentDip?.dip.id;
					reportEvent("dips_button_click", {
						source: "dips_dialog",
						loyalty_offer_id: loyaltyOfferId,
						dip_id: dipId,
						reward_id: rewardId,
						page_location: location.pathname
					});
					reportPlaygamiEvent("tp.web_frontend", {
						event_name: "dips_button_click",
						event_type: "dips_dialog",
						action: actionType,
						source: "dips_dialog",
						event_id: dipId,
						offer_id: loyaltyOfferId,
						reward_id: rewardId,
						page_location: location.pathname
					});
					openDialog(WsDialogs.LOYALTY_PURCHASE_CONFIRM, {
						data: {
							...(loyalty as IShopLoyaltyOffer),
							dipId
						},
						offerId: id,
						itemDescription: description,
						itemPrice: loyaltyPointPrice,
						itemImageUrl: rewardImageUrl,
						items: rewardItems as DisplayReward[]
					});
				}
				break;
			case "offerPurchase":
				const buttonOffer: IShopPurchaseOffer | undefined =
					purchaseOffers?.offers.find(
						(c: IShopPurchaseOffer) => c.id === offerId
					);
				const dipId = currentDip?.dip.id;
				if (buttonOffer?.upsellOfferConfig && currentDip) {
					dispatch(onSetBundleUpDip(currentDip));
					open({
						variant: `bundle-up::${offerId}`,
						id: `bundle-up::${offerId}`,
						replaceIfExists: true
					});
					dispatch(onCloseDIP());
					return;
				}
				const buyProps = {
					offerId: offerId as string,
					rewardMultiplier: buttonOffer?.rewardMultiplier,
					dipId,
					fromHud
				};
				wsStoreBuy(buyProps).then((invoice) => {
					if (invoice) {
						const isFirstPurchaseFtue =
							isFeatureFlagEnabled(FeaturesFlags.FTUE_V2) &&
							invoice.isFirstPurchase;
						dispatch(onDisableButtonsDIP());
						const timestampMs = new Date().getTime();
						reportEvent("dips_button_click", {
							source: "dips_dialog",
							offer_id: offerId,
							dip_id: dipId,
							reward_id: rewardId,
							page_location: location.pathname
						});
						reportPlaygamiEvent("tp.web_frontend", {
							event_name: fromHud
								? "limited_deal_hud_click"
								: "dips_button_click",
							event_type: "dips_dialog",
							action: "dips_buy_click",
							source: "dips_dialog",
							offer_id: offerId,
							event_id: dipId,
							type: getDipAnalyticsType({
								fromHud,
								activeTrigger,
								pathname: location.pathname,
								shopPath: links.shop.path,
								eliteDealsPath: links.elite_deals.path
							}),
							page_location: location.pathname
						});
						// dispatch(onCloseDIP());
						openDialog(WsDialogs.XSOLLA_PURCHASE, {
							xsollaToken: invoice.xsollaToken,
							xsollaHost: invoice.xsollaHost,
							invoice,
							rewardMultiplier: buyProps.rewardMultiplier,
							suppressOrderConfirmation,
							source: "dips",
							pricePoint: price,
							suppressDips:
								popup === "tutorial-loyalty-shop"
									? {
											purchaseSuccess: true,
											purchaseFailed: true,
											purchaseCancelled: true
									  }
									: undefined,
							postUserMetadata,
							onPurchaseStarted: (data) => {
								const minutesLeft = getTimeLeft(
									nowTime,
									currentDip?.dip.endTime
								);
								reportEvent("purchase_started", {
									section_name: "dips_dialog",
									offer_id: offerId,
									price_in_cents: Number(price) * 100,
									reward_id: rewardId,
									invoice_id: invoice?.id,
									paystation_id: data?.payStation
								});
								reportPlaygamiEvent("tp.web_ga.purchase_flow", {
									event_name: "purchase_started",
									event_type: "web_paystation_purchase",
									action: "purchase_started",
									section_name: "dips_dialog",
									purchase_flow_id: data?.payStation,
									session_engaged: authToken?.value ? "1" : "0",
									price_in_cents: Math.round(invoice?.price * 100),
									page_location: window.location.href,
									engagement_time_msec: (
										Number(data?.data?.milliseconds) - timestampMs
									).toString(),
									reward_id: buttonOffer?.rewardId,
									offer_id: offerId,
									ignore_referrer:
										process.env.REACT_APP_WITH_DEBUG === "true"
											? "true"
											: "false",
									minutes_left: minutesLeft,
									page_title: location.pathname.replace("/", ""),
									page_referrer: invoice?.invoiceMetadata?.referer,
									priority: currentDip?.dip?.priority,
									invoice_id: invoice?.id
								});
								if (popup === "tutorial-loyalty-shop") {
									reportPlaygamiEvent("tp.web_frontend", {
										event_name: "lp_ftue_offer_purchase_started",
										event_type: "lp_ftue",
										action: "lp_ftue_offer_purchase_started",
										source: "loyalty_shop",
										offer_id: offerId,
										type: isFirstTimeLPFTUESeen
											? "replay_view"
											: "first_time_view",
										page_location: location.pathname,
										price_in_cents: Math.round(invoice?.price * 100),
										value_int3:
											Number(userTracker?.loyaltyPoints) +
											Number(buttonOffer?.loyaltyPointReward)
									});
								}
							},
							onPurchaseSuccess: (data) => {
								const minutesLeft = getTimeLeft(
									nowTime,
									currentDip?.dip.endTime
								);
								reportEvent("purchase_success", {
									invoice_id: invoice?.id
								});
								reportPlaygamiEvent("tp.web_ga.purchase_flow", {
									event_name: "purchase_success",
									event_type: "web_paystation_purchase",
									action: "purchase_success",
									section_name: "dips_dialog",
									purchase_flow_id: data?.version,
									session_engaged: authToken?.value ? "1" : "0",
									price_in_cents: Math.round(invoice?.price * 100),
									page_location: window.location.href,
									engagement_time_msec: (
										Number(data?.milliseconds) - timestampMs
									).toString(),
									reward_id: buttonOffer?.rewardId,
									offer_id: offerId,
									ignore_referrer:
										process.env.REACT_APP_WITH_DEBUG === "true"
											? "true"
											: "false",
									minutes_left: minutesLeft,
									page_title: location.pathname.replace("/", ""),
									page_referrer: invoice?.invoiceMetadata?.referer,
									priority: currentDip?.dip?.priority,
									invoice_id: invoice?.id,
									transaction_id: data?.purchase_invoice_id?.toString()
								});
								if (popup === "tutorial-loyalty-shop") {
									reportPlaygamiEvent("tp.web_frontend", {
										event_name: "lp_ftue_offer_purchase_done",
										event_type: "lp_ftue",
										action: "lp_ftue_offer_purchase_done",
										source: "loyalty_shop",
										offer_id: offerId,
										type: isFirstTimeLPFTUESeen
											? "replay_view"
											: "first_time_view",
										page_location: location.pathname,
										price_in_cents: Math.round(invoice?.price * 100),
										value_int3:
											Number(userTracker?.loyaltyPoints) +
											Number(buttonOffer?.loyaltyPointReward)
									});
								}

								if (
									!isFirstPurchaseFtue &&
									currentDip?.dip.triggerType === "bouncyChestClick"
								) {
									dispatch(onOpenPopUp("bouncy-chest-reward-collected"));
								}

								if (currentDip) {
									dispatch(onAddRequisitionCount());
								}
							},
							cbClose: () => {
								dispatch(onEnableButtonsDIP());
								reportEvent("dialog_x_closed", {
									source: "xsolla_dialog",
									page_location: location.pathname
								});
							}
						});
					}
				});
				break;
			case "shopSection":
				dispatch(setOpen(false));
				switch (shopSection) {
					case "clubShop":
						navigate(links.club_shop.path);
						break;
					case "loyalty":
						navigate(links.loyalty.path);
						break;
					case "clubFreeGift":
						navigate(links.club_free_gift.path);
						break;
					case "dailyGift":
					case "freeDailyGifts":
						navigate(links.free_daily_gifts.path);
						break;
					case "poisBounty":
						navigate(links.pois_bounty.path);
						break;
					case "news":
						navigate(links.news.path);
						break;
					case "merchShop":
						navigate(links.merch_shop.path);
						break;
					case "dripOffer":
						navigate(links.drip_offer.path);
						break;
					case "friendShop":
						navigate(`${links.friend_center.path}/shop/`);
						break;
					case "friendInvite":
						navigate(`${links.friend_center.path}/invite/`);
						break;
					case "elite":
						navigate(links.elite_deals.path);
						break;
					case "featuredDeals":
						navigate(links.shop.path);
						dispatch(onScrollToShopSection("Featured Deals"));
						break;
					case "limitedDeals":
						navigate(links.shop.path);
						dispatch(onScrollToShopSection("Limited Deals"));
						break;
					case "singleItems":
						navigate(links.shop.path);
						const filteredSections = shopSections?.mainShopSections.filter(
							(data) =>
								purchaseOffers?.offers?.find(
									(of: IOffer) =>
										of.sectionName === data.name &&
										of.sectionName !== "limited_deals"
								)
						);
						filteredSections?.[0]?.title &&
							dispatch(
								onScrollToShopSection(
									filteredSections[0].title as ShopSectionTitles
								)
							);
						break;
					default:
						navigate(links.shop.path);
						// we now want every OPEN SHOP to take the user to the featured deals section
						dispatch(onScrollToShopSection("Featured Deals"));
				}
				dispatch(onCloseDIP());
				break;

			default:
				dispatch(onCloseDIP());
		}
	};

	return {
		onAction
	};
};
